// Global Instructions Rule Applied!
//
// LP Brief card — the LP account Dashboard's relationship write-up, derived
// from the relationship-manager agent's memory and the relationship facts
// per (account, section); cfg.lp_brief_sections drives the section set and
// order. Read-only by design, the DealBriefCard's sibling: corrections flow
// through the Relationship tab (facts) and memory ops; the brief is
// re-materialised by the next deep / zoom run.
import React, { useCallback, useEffect, useState } from 'react'
import { Tag, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { fetchLpBrief } from '../services/internalAgentsService.ts'
import { LpBriefSection } from '../../../state_manager/data_models/internal_agents_model.ts'

interface LpBriefCardProps {
  accountId: string
  /** Hands `load` to the account form so its Refresh button reloads this
   *  panel. Same contract DocumentsTab exposes via onRefreshReady. */
  onRefreshReady?: ((refreshFn: () => Promise<void>) => void) | null
  /** Render nothing until the agent has written a brief, so an LP account
   *  with no agent output keeps today's layout. */
  hideWhenEmpty?: boolean
}

export const LpBriefCard: React.FC<LpBriefCardProps> = ({
  accountId,
  onRefreshReady = null,
  hideWhenEmpty = false
}) => {
  const [sections, setSections] = useState<LpBriefSection[]>([])
  const [loading, setLoading] = useState(true)
  const token = () => getAccessToken(store.getState().auth)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setSections(await fetchLpBrief(accountId, token()))
    } catch (error) {
      console.error('[internal-agents] LP brief load failed:', error)
      setSections([])
    } finally {
      setLoading(false)
    }
  }, [accountId])

  useEffect(() => {
    load()
  }, [load])

  // The app shell hides tabs with display:none rather than unmounting them, so
  // a mount-time-only fetch goes stale. `onRefreshReady` hands `load` up to the
  // account form so its Refresh button reloads this panel too.
  useEffect(() => {
    if (onRefreshReady) onRefreshReady(load)
  }, [onRefreshReady, load])

  const written = sections.filter((s) => s.lp_briefs__content)
  const generatedAt = written.reduce<string | null>(
    (latest, s) =>
      s.lp_briefs__generated_at && (!latest || s.lp_briefs__generated_at > latest)
        ? s.lp_briefs__generated_at
        : latest,
    null
  )

  if (hideWhenEmpty && !loading && written.length === 0) {
    return null
  }

  return (
    <FormSection
      data-component='LpBriefCard'
      title='LP Brief'
      titleColor='green'
      loading={loading}
      className='shadow-sm border border-gray-200'
      headerActions={
        <span className='flex items-center gap-2'>
          <Tag color='blue'>Agent-written</Tag>
          {generatedAt ? (
            <span className='text-gray-400 text-[11.5px]'>
              updated {dayjs(generatedAt).format('MM/DD/YYYY')}
            </span>
          ) : null}
        </span>
      }
    >
      {written.length === 0 && !loading ? (
        <div className='text-gray-400 text-xs py-2'>Awaiting the relationship manager agent's first deep run.</div>
      ) : (
        <div className='flex flex-col divide-y divide-gray-100 -mx-1'>
          {sections.map((section) => (
            <div key={section.lp_brief_sections__section_key} className='px-1 py-2'>
              <span className='text-[11.5px] font-semibold uppercase tracking-wide text-gray-500'>
                {section.lp_brief_sections__label}
                {section.lp_brief_sections__external_safe ? (
                  <Tooltip title='Safe to quote to the investor: no internal judgement, process detail or personal information'>
                    <Tag className='ml-2' style={{ fontSize: 10, lineHeight: '16px' }}>
                      LP-safe
                    </Tag>
                  </Tooltip>
                ) : null}
              </span>
              {section.lp_briefs__content ? (
                <div className='whitespace-pre-line text-[13px] mt-0.5'>
                  {section.lp_briefs__content}
                </div>
              ) : (
                <div className='text-gray-400 text-xs mt-0.5'>Awaiting the first deep run.</div>
              )}
            </div>
          ))}
        </div>
      )}
    </FormSection>
  )
}
